import { useT, UT } from "@transifex/react";
import { CoverImage } from "p6-react-shared";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import CreationFlowSection from "../../../basic/create/creationFlowSection/CreationFlowSection";
import DeleteCardWarningModal from "../../../basic/create/deleteCardWarningModal/DeleteCardWarningModal";
import CreateInfoModal from "../../../basic/create/InfoModal/CreateInfoModal";
import ResponsiveButton from "../../../basic/responsiveButton/ResponsiveButton";
import StyledButton from "../../../basic/styledButton/StyledButton";
import UnitAndCardsWrapperRT from "../../../complex/create/unitAndCardsWrapperRT/UnitAndCardsWrapperRT";
import VocabTrainingTitleBar from "../../../complex/create/vocabTrainingTitleBar/VocabTrainingTitleBar";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { VocabTrainingTitleTags } from "../../../enums/vocabTrainingTitleTags";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import usePreviouslyUsedCards from "../../../hooks/usePreviouslyUsedCards";
import CtasWrapperRT from "../../../wrapper/create/ctasWrapperRT/ctasWrapperRT";
import {
    BigButtonWithIcon,
    BookHeaderInfo,
    BookImageWrapper,
    BookName,
    BookNameWrapper,
    ExerciseToSpan,
    GeneralWrapper,
    SelectBookVocabularyWrapper,
    UnitsWrapper,
} from "./StyledComponents";

export interface SelectBookVocabularyProps {}

const SelectBookVocabularyRT: React.FC<SelectBookVocabularyProps> = (props) => {
    const {
        selectedBookData,
        loadVocabularyForBand,
        selectedBandUnits,
        removeCardsFromSelectedWords,
        availableCards,
        vocabularyDrawerContent,
        openWarningModal,
        selectedTitleTag,
        setSelectedTitleTag,
        testName,
    } = useTestCreationContext();
    const { creatingMode } = useGeneralContext();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    usePreviouslyUsedCards(selectedBookData.band?.UUID);

    const history = useHistory();

    const t = useT();
    const t_exerciseTo = t("Exercise to:", { _tags: "CreateTest,SelectBookVocabulary" });
    const t_back = t("Back", { _tags: "CreateTest,SelectBookVocabulary" });
    const t_createExercise = t("Create Exercise", { _tags: "CreateTest,SelectBookVocabulary" });
    const t_chooseMinVocabulary = t("Please select at least 3 words", { _tags: "CreateTest,SelectBookVocabulary" });
    const t_addWords = t("Add more Cards", { _tags: "CreateTest,SelectBookVocabulary" });
    const t_continue = t("Continue", { _tags: "CreateTest,SelectBookVocabulary" });
    const t_selectVocabularyTitle = t("Select Vocabulary", { _tags: "CreateTest,SelectBookVocabulary" });

    const [currentWordForDeletion, setCurrentWordForDeletion] = useState("");
    const [isCurrentWordForDeletionModalOpen, setIsCurrentWordForDeletionModalOpen] = useState(false);

    const [currentUnitCardsForDeletion, setCurrentUnitCardsForDeletion] = useState<Array<string>>([]);
    const [isDeleteAllUnitCardsModalOpen, setIsDeleteAllUnitCardsModalOpen] = useState(false);

    useEffect(() => {
        if (selectedBookData.band && selectedBookData.band.ID) {
            loadVocabularyForBand(selectedBookData.band.ID);
        } else {
            history.push("/create/choose-source");
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBookData]);

    function handleCreateExerciseBtnClick() {
        if (Object.keys(vocabularyDrawerContent).length >= 3) {
            history.push("/create/select-exercise");
        } else {
            openWarningModal(t_chooseMinVocabulary);
        }
    }

    function closeDeleteWordWarningModal() {
        setIsCurrentWordForDeletionModalOpen(false);
    }

    function openDeleteModalFn(wordId: string) {
        setCurrentWordForDeletion(wordId);
        setIsCurrentWordForDeletionModalOpen(true);
    }

    function openDeleteUnitCardsFn(cardsToDelete: Array<string>) {
        setCurrentUnitCardsForDeletion(cardsToDelete);
        setIsDeleteAllUnitCardsModalOpen(true);
    }

    function deleteCardsFromUnit() {
        removeCardsFromSelectedWords(currentUnitCardsForDeletion);
        setIsDeleteAllUnitCardsModalOpen(false);
    }

    function handleBackButton() {
        if (creatingMode === CreationModes.RECURRING_TASK) {
            history.push("/create/select-book");
        } else {
            history.push("/create/choose-source");
        }
    }

    function handleContinueButton() {
        // Pre-select the "Based on vocabulary" title when arriving at the summary via "Weiter",
        // unless the user already chose a tag or typed a custom title.
        if (!selectedTitleTag && !testName) {
            setSelectedTitleTag(VocabTrainingTitleTags.BASED_ON_VOCAB);
        }
        history.push("/create/rt-summary");
    }

    return (
        <GeneralWrapper>
            <CreationFlowSection>
                <VocabTrainingTitleBar />
            </CreationFlowSection>
            <p className={`${getStyleBasedOnBreakpoint(Breakpoint.MD, "px-s py-s")} text-l text-p6-grey-dark`}>
                {t_selectVocabularyTitle}
            </p>
            <SelectBookVocabularyWrapper>
                <BookHeaderInfo>
                    <BookImageWrapper>
                        {selectedBookData.band?.Image && (
                            <CoverImage
                                imageId={"https://www.phase-6.de" + selectedBookData.band?.Image}
                                size={"small"}
                            />
                        )}
                    </BookImageWrapper>
                    <BookNameWrapper>
                        <ExerciseToSpan>{t_exerciseTo}</ExerciseToSpan>
                        <BookName>
                            {selectedBookData.publisherBook?.BookName} ({selectedBookData.publisherBook?.PublisherName})
                            ({selectedBookData.band?.ShortName})
                        </BookName>
                    </BookNameWrapper>
                </BookHeaderInfo>
                <UnitsWrapper>
                    {selectedBandUnits.map((u) => {
                        if (u.id) {
                            const hasCards = availableCards.hasOwnProperty(u.id) && availableCards[u.id].items;
                            return (
                                <UnitAndCardsWrapperRT
                                    unitContent={u}
                                    key={u.id}
                                    openDeleteModalFn={openDeleteModalFn}
                                    openDeleteUnitCardsFn={openDeleteUnitCardsFn}
                                    cardsList={hasCards ? availableCards[u.id].items : []}
                                    totalUnitCardsCount={hasCards ? availableCards[u.id].count : 0}
                                />
                            );
                        }
                        return false;
                    })}
                </UnitsWrapper>
            </SelectBookVocabularyWrapper>
            <CtasWrapperRT isSticky>
                <ResponsiveButton
                    onClick={handleBackButton}
                    iconName="chevron-left"
                    label={t_back}
                >
                    <StyledButton
                        onClick={handleBackButton}
                        arrowDirection={"LEFT"}
                        buttonStyle={"BLANK"}
                    >
                        {t_back}
                    </StyledButton>
                </ResponsiveButton>
                {creatingMode !== CreationModes.RECURRING_TASK && (
                    <BigButtonWithIcon
                        buttonStyle={"BLANK"}
                        icon={"vocabulary-list-unset"}
                        iconPosition={"LEFT"}
                        onClick={() => {
                            history.push("/create/choose-source");
                        }}
                    >
                        {t_addWords}
                    </BigButtonWithIcon>
                )}
                {creatingMode === CreationModes.TEST && (
                    <StyledButton
                        onClick={handleCreateExerciseBtnClick}
                        arrowDirection={"NONE"}
                        buttonStyle={"PRIMARY"}
                        disabled={Object.keys(vocabularyDrawerContent).length === 0}
                    >
                        {t_createExercise}
                    </StyledButton>
                )}
                {creatingMode === CreationModes.RECURRING_TASK && (
                    <StyledButton
                        data-testid="button-continue-to-summary"
                        onClick={handleContinueButton}
                        arrowDirection={"NONE"}
                        buttonStyle={"PRIMARY"}
                        disabled={Object.keys(vocabularyDrawerContent).length === 0}
                    >
                        {t_continue}
                    </StyledButton>
                )}
            </CtasWrapperRT>
            <DeleteCardWarningModal
                isModalOpen={isCurrentWordForDeletionModalOpen}
                closeModalFn={closeDeleteWordWarningModal}
                wordId={currentWordForDeletion}
            />
            <CreateInfoModal
                isOpen={isDeleteAllUnitCardsModalOpen}
                onRequestClose={() => {
                    setIsDeleteAllUnitCardsModalOpen(false);
                }}
                continueAction={deleteCardsFromUnit}
                continueText={t_continue}
                cancelAction={() => {
                    setIsDeleteAllUnitCardsModalOpen(false);
                }}
                compact
            >
                <UT
                    _str="Warning: Unchecking this box deletes all words from the basket and exercises for which those words have been used."
                    _inline
                    _tags="CreateTest,SelectBookVocabulary"
                />
            </CreateInfoModal>
        </GeneralWrapper>
    );
};

export default SelectBookVocabularyRT;
